import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { AdContext } from './AdContext';
import { X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const AdDisplay = ({ location, className = "" }) => {
 const { API, user } = useContext(AuthContext);
 const { fetchAd } = useContext(AdContext);
 const [ads, setAds] = useState([]);
 const [loading, setLoading] = useState(true);
 const [dismissedAds, setDismissedAds] = useState(new Set());

 useEffect(() => {
   let alive = true;
   if (!location) return () => {};
   setLoading(true);
   fetchAd(location).then((results) => {
     if (!alive) return;
     setAds(results || []);
   }).finally(() => { if (alive) setLoading(false); });
   return () => { alive = false; };
 }, [location, user, fetchAd]);

 const handleAdClick = async (campaignId, clickUrl) => {
 try {
 // Track the click
 await axios.post(`${API}/ads/${campaignId}/click`);
 
 // Open the URL if provided
 if (clickUrl) {
 window.open(clickUrl, '_blank');
 }
 } catch (error) {
 console.error('Failed to track ad click:', error);
 }
 };

 const dismissAd = (adId) => {
 setDismissedAds(new Set([...dismissedAds, adId]));
 };

 if (loading) return null;

 const visibleAds = ads.filter(ad => !dismissedAds.has(ad.id));
 
 if (visibleAds.length === 0) return null;

 return (
 <div className={`space-y-4 ${className}`}>
 {visibleAds.map((ad) => (
 <AdItem
 key={ad.id}
 ad={ad}
 onAdClick={handleAdClick}
 onDismiss={dismissAd}
 />
 ))}
 </div>
 );
};

const AdItem = ({ ad, onAdClick, onDismiss }) => {
 const [imageError, setImageError] = useState(false);

 const renderMedia = () => {
 // Sensible size ceilings per slot so an over-tall creative can't spill
 // into the next section (Landing "What FDAcademy is about", etc.).
 // Only applied when the campaign didn't declare explicit width/height.
 const SIZE_CEILINGS = {
   hero:        { maxWidth: '100%', maxHeight: 420 },
   banner_wide: { maxWidth: '100%', maxHeight: 260 },
   banner_med:  { maxWidth: 728,    maxHeight: 250 },
   banner:      { maxWidth: '100%', maxHeight: 260 }, // legacy fallback
   sidebar:     { maxWidth: 320,    maxHeight: 620 },
   inline:      { maxWidth: '100%', maxHeight: 220 },
   overlay:     { maxWidth: 640,    maxHeight: 120 },
 };
 const bucket = SIZE_CEILINGS[ad.size] || SIZE_CEILINGS.inline;
 const style = {
   maxWidth:  ad.width  || bucket.maxWidth,
   maxHeight: ad.height || bucket.maxHeight,
   objectFit: 'contain',
 };
 switch (ad.media_type) {
 case 'image':
 if (imageError || !ad.file_url) return null;
 return (
 <img
 src={ad.file_url}
 alt={ad.alt_text || ad.name}
 className="w-full h-auto rounded"
 style={style}
 onError={() => setImageError(true)}
 loading="lazy"
 />
 );
 
 case 'video':
 if (!ad.file_url) return null;
 return (
 <video
 src={ad.file_url}
 className="w-full h-auto rounded"
 style={style}
 controls
 muted
 playsInline
 />
 );
 
 case 'video_embed':
 if (!ad.embed_url) return null;
 const embedUrl = getEmbedUrl(ad.embed_url);
 return (
 <iframe
 src={embedUrl}
 className="w-full rounded"
 style={{ 
 width: ad.width || 560, 
 height: ad.height || 315 
 }}
 frameBorder="0"
 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
 allowFullScreen
 />
 );
 
 case 'gif':
 if (!ad.file_url) return null;
 return (
 <img
 src={ad.file_url}
 alt={ad.alt_text || ad.name}
 className="w-full h-auto rounded"
 style={style}
 loading="lazy"
 />
 );
 
 case 'html_banner':
 if (!ad.embed_code) return null;
 return (
 <div
 className="w-full"
 style={{ 
 maxWidth: ad.width || bucket.maxWidth, 
 maxHeight: ad.height || bucket.maxHeight 
 }}
 dangerouslySetInnerHTML={{ __html: ad.embed_code }}
 />
 );
 
 default:
 return null;
 }
 };

 const getEmbedUrl = (url) => {
 // Convert YouTube URLs to embed format
 if (url.includes('youtube.com/watch')) {
 const videoId = url.split('v=')[1]?.split('&')[0];
 return `https://www.youtube.com/embed/${videoId}`;
 }
 if (url.includes('youtu.be/')) {
 const videoId = url.split('youtu.be/')[1]?.split('?')[0];
 return `https://www.youtube.com/embed/${videoId}`;
 }
 
 // Convert Vimeo URLs to embed format
 if (url.includes('vimeo.com/')) {
 const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
 return `https://player.vimeo.com/video/${videoId}`;
 }
 
 return url;
 };

 const handleClick = () => {
 onAdClick(ad.campaign_id, ad.click_url);
 };

 const hasClickAction = ad.click_url || ad.custom_cta_text;

 // Wrapper `maxWidth` hugs the media so the surrounding white card
 // doesn't stretch to the full parent width and swallow the ad in
 // dead space on the left/right. Falls back to the size ceiling used
 // by `renderMedia`.
 const SIZE_CEILINGS = {
   hero:        { maxWidth: '100%' },
   banner_wide: { maxWidth: '100%' },
   banner_med:  { maxWidth: 728 },
   banner:      { maxWidth: '100%' }, // legacy
   sidebar:     { maxWidth: 320 },
   inline:      { maxWidth: 640 },
   overlay:     { maxWidth: 640 },
 };
 const wrapCeiling = SIZE_CEILINGS[ad.size] || SIZE_CEILINGS.inline;
 const wrapStyle = { maxWidth: ad.width || wrapCeiling.maxWidth };

 return (
 <div
 className="relative block w-full bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
 style={wrapStyle}
 data-testid={`ad-display-${ad.size || 'inline'}`}
 >
 {/* Dismiss Button */}
 <button
 onClick={() => onDismiss(ad.id)}
 className="absolute top-2 right-2 z-10 p-1 bg-white/80 hover:bg-white rounded-full text-slate-500 hover:text-slate-700"
 title="Dismiss ad"
 >
 <X className="w-4 h-4" />
 </button>

 {/* Media Content */}
 <div className={hasClickAction ? 'cursor-pointer' : ''} onClick={hasClickAction ? handleClick : undefined}>
 {renderMedia()}
 
 {/* Text Overlay for Custom Content */}
 {(ad.custom_title || ad.custom_description) && (
 <div className="p-4">
 {ad.custom_title && (
 <h3 className="font-semibold text-lg text-slate-900 mb-2">{ad.custom_title}</h3>
 )}
 {ad.custom_description && (
 <p className="text-slate-600 text-sm mb-3">{ad.custom_description}</p>
 )}
 {ad.custom_cta_text && ad.click_url && (
 <Button size="sm" className="inline-flex items-center gap-2">
 {ad.custom_cta_text}
 <ExternalLink className="w-3 h-3" />
 </Button>
 )}
 </div>
 )}
 </div>

 {/* Ad Label */}
 <div className="absolute bottom-2 left-2 bg-slate-800/70 text-white text-xs px-2 py-1 rounded">
 Ad
 </div>
 </div>
 );
};

export default AdDisplay;